import { memo, useState } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';

const CARD_BASE = 'https://wver.sprintstaticdata.com/v230/static/front/img/cards';

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

// 300000 -> 3L (reference range formatting)
function formatAmount(n) {
  const num = Number(n) || 0;
  if (num >= 100000) return `${num / 100000}L`;
  if (num >= 1000) return `${num / 1000}K`;
  return String(num);
}

function bookOf(exposures, item) {
  const v = exposures?.[`sid:${item?.sid}`] ?? exposures?.[item?.nat];
  return v ? roundBook(v) : 0;
}

function bookCls(v) {
  if (!v) return 'book-black';
  return v < 0 ? 'book-red' : 'book-green';
}

// Poison (Teenpatti Poison One Day) — reference: teen1daycasino-container;
// left A/B back+lay rows (#range1/2), right joker-other: Even/Odd/Red/Black
// (#range3-6) + suits spade/heart/diamond/club (#range7-10). Right blocks ke
// range icons collapsed/aria ke bina hote hain, class order "collapse icon-range".
// prefix: 'Poison' (poison/poison20) ya 'Joker' (joker20) — nats aur label
// isi se. footer: mobile pe last-results strip (teen1daycasino-container ke
// andar, teen1dayright ke baad — reference).
// lockTopBook: a locked side cell WITH a liability pins its padlock to the top
// (PoisonPage turns it on for joker20, poison20 and poison).
const BetTablePoison = memo(function BetTablePoison({ gameData, onBet, exposures, prefix = 'Poison', footer = null, lockTopBook = false }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});
  const [openRanges, setOpenRanges] = useState({});
  const toggle = (id) => setOpenRanges((p) => ({ ...p, [id]: !p[id] }));
  const byNat = (nat) => sub.find((s) => s.nat === nat);

  const playerA = byNat('Player A');
  const playerB = byNat('Player B');
  const evenItem = byNat(`${prefix} Even`);
  const oddItem = byNat(`${prefix} Odd`);
  const redItem = byNat(`${prefix} Red`);
  const blackItem = byNat(`${prefix} Black`);
  const spadeItem = byNat(`${prefix} Spade`);
  const heartItem = byNat(`${prefix} Heart`);
  const diamondItem = byNat(`${prefix} Diamond`);
  const clubItem = byNat(`${prefix} Club`);

  const playerRow = (item, label) => {
    const book = bookOf(exposures, item);
    const rid = `range${item?.sid ?? 0}`;
    return (
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <b>{label}</b>
          {' '}
          <div className="float-right">
            <span className={`mr-2 ${bookCls(book)}`}>{fmtBook(book)}</span>
            {' '}
            <i
              data-toggle="collapse"
              data-target={`#${rid}`}
              aria-expanded={openRanges[rid] ? 'true' : 'false'}
              className={`fas fa-info-circle${openRanges[rid] ? '' : ' collapsed'}`}
              onClick={() => toggle(rid)}
              style={{ cursor: 'pointer' }}
            ></i>
            {' '}
            <div id={rid} className={`icon-range collapse${openRanges[rid] ? ' show' : ''}`}>
              {'\n                        R:'}
              <span>{formatAmount(item?.min)}</span>
              -
              <span>{formatAmount(item?.max)}</span>
            </div>
          </div>
        </div>
        {' '}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${suspCls(item)}`}
            onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
          >
            <span className="casino-box-odd">{item?.b || 0}</span>
          </div>
          {' '}
          <div
            className={`lay casino-bl-box-item${suspCls(item)}`}
            onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'lay', item.l, item)}
          >
            <span className="casino-box-odd">{item?.l || 0}</span>
          </div>
        </div>
      </div>
    );
  };

  // right-side range block (icon bina collapsed/aria ke, "collapse icon-range")
  const rangeBlock = (item) => {
    const rid = `range${item?.sid ?? 0}`;
    return (
      <div className="text-right casino-rb-box-player-range w-100 mt-1">
        <i
          data-toggle="collapse"
          data-target={`#${rid}`}
          className="fas fa-info-circle float-right"
          onClick={() => toggle(rid)}
          style={{ cursor: 'pointer' }}
        ></i>
        {' '}
        <div id={rid} className={`collapse icon-range${openRanges[rid] ? ' show' : ''}`}>
          {'\n                          R:'}
          <span>{formatAmount(item?.min)}</span>
          -
          <span>{formatAmount(item?.max)}</span>
        </div>
      </div>
    );
  };

  // With `lockTopBook` (Joker 20-20 + Poison 20-20 / One Day, user 2026-10-14)
  // a locked side cell WITH a liability pins its padlock to the TOP (style.css
  // `.suspended.lock-top:before`) and the book is lifted above the overlay
  // (index.css); without one it stays centred.
  const lockTop = (item) => (lockTopBook && bookOf(exposures, item) ? ' lock-top' : '');

  const betBox = (item, content, imgBox = false) => (
    <div className="casino-bl-box">
      <div
        className={`back casino-bl-box-item${imgBox ? ' casino-card-img' : ''}${lockTop(item)}${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
      >
        {content}
        {' '}
        <span className={bookCls(bookOf(exposures, item))}>{fmtBook(bookOf(exposures, item))}</span>
      </div>
      {' '}
      {rangeBlock(item)}
    </div>
  );

  return (
    <div className="teen1daycasino-container mt-2">
      <div className="teen1dayleft">
        {playerRow(playerA, 'Player A')}
        {' '}
        {playerRow(playerB, 'Player B')}
      </div>
      {' '}
      <div className="teen1daycenter"></div>
      {' '}
      <div className="teen1dayright joker-other">
        <div>
          <div className="casino-box-row casino-odds">
            <div className="text-left w-100"><b className="text-playerb">{prefix}</b></div>
          </div>
          {' '}
          <div className="casino-box-row">
            <div className="casino-bl-box"><b>{evenItem?.b || 0}</b></div>
            {' '}
            <div className="casino-bl-box"><b>{oddItem?.b || 0}</b></div>
            {' '}
            <div className="casino-bl-box"><b>{redItem?.b || 0}</b></div>
            {' '}
            <div className="casino-bl-box"><b>{blackItem?.b || 0}</b></div>
          </div>
          {' '}
          <div className="casino-box-row">
            {betBox(evenItem, <span className="casino-box-odd">Even</span>)}
            {' '}
            {betBox(oddItem, <span className="casino-box-odd">Odd</span>)}
            {' '}
            {betBox(redItem, (
              <span>
                <img src={`${CARD_BASE}/heart.png`} />
                {' '}
                <img src={`${CARD_BASE}/diamond.png`} />
              </span>
            ), true)}
            {' '}
            {betBox(blackItem, (
              <span>
                <img src={`${CARD_BASE}/spade.png`} />
                {' '}
                <img src={`${CARD_BASE}/club.png`} />
              </span>
            ), true)}
          </div>
        </div>
        {' '}
        <div className="mt-1">
          <div className="casino-box-row">
            {['spade', 'heart', 'diamond', 'club'].map((suit) => (
              <div key={suit} className="casino-bl-box">
                <div className="casino-bl-box-item casino-card-img">
                  <img src={`${CARD_BASE}/${suit}.png`} />
                </div>
              </div>
            ))}
          </div>
          {' '}
          <div className="casino-box-row">
            {betBox(spadeItem, <span className="casino-box-odd">{spadeItem?.b || 0}</span>)}
            {' '}
            {betBox(heartItem, <span className="casino-box-odd">{heartItem?.b || 0}</span>)}
            {' '}
            {betBox(diamondItem, <span className="casino-box-odd">{diamondItem?.b || 0}</span>)}
            {' '}
            {betBox(clubItem, <span className="casino-box-odd">{clubItem?.b || 0}</span>)}
          </div>
        </div>
      </div>
      {' '}
      {/* mobile pe last-results strip yahan (container ke andar, teen1dayright ke baad) */}
      {footer}
    </div>
  );
});

export default BetTablePoison;
